import React from 'react';
import { getLevelColor } from '@/utils/colors';

interface Props {
    level: number;
    color?: string;
    size?: 'sm' | 'md';
    upper?: boolean;
    /**
     * 'tint'  — translucent accent-tinted pill with "N LVL" (default, used in
     *           headers / profile / sidebar).
     * 'solid' — bright accent-gradient pill with dark text and the number only,
     *           mirroring the Fluent jackpot reference `.lvl` / `.mlvl`.
     */
    variant?: 'tint' | 'solid';
    className?: string;
}

const SIZES = {
    sm: 'text-[10px] px-1.5 py-px',
    md: 'text-xs px-2 py-0.5',
};

const SOLID_SIZES = {
    sm: 'text-[10px] min-w-[20px] h-[18px] px-1',
    md: 'text-[11px] min-w-[22px] h-5 px-1.5',
};

const LevelBadge = ({
    level,
    color,
    size = 'md',
    upper = true,
    variant = 'tint',
    className = '',
}: Props) => {
    const resolvedColor = color ?? getLevelColor(level);

    if (variant === 'solid') {
        return (
            <span
                className={`${SOLID_SIZES[size]} rounded-[7px] grid place-items-center font-extrabold leading-none whitespace-nowrap shrink-0 ${className}`}
                style={
                    {
                        ['--u-color' as string]: resolvedColor,
                        color: '#0a0718',
                        background:
                            'linear-gradient(180deg, color-mix(in srgb, var(--u-color) 60%, #fff), var(--u-color))',
                        boxShadow: '0 0 12px var(--u-color), 0 0 4px var(--u-color)',
                        border: '1.5px solid rgba(10,8,20,0.85)',
                    } as React.CSSProperties
                }
            >
                {level}
            </span>
        );
    }

    return (
        <span
            className={`${SIZES[size]} rounded-md font-bold tracking-[0.06em] whitespace-nowrap shrink-0 ${className}`}
            style={
                {
                    ['--u-color' as string]: resolvedColor,
                    background: 'color-mix(in srgb, var(--u-color) 14%, transparent)',
                    color: 'var(--u-color)',
                    border: '1px solid color-mix(in srgb, var(--u-color) 32%, transparent)',
                    boxShadow:
                        '0 0 10px -3px var(--u-color), inset 0 0 10px -3px color-mix(in srgb, var(--u-color) 30%, transparent)',
                    textShadow: '0 0 8px color-mix(in srgb, var(--u-color) 50%, transparent)',
                } as React.CSSProperties
            }
        >
            {level} {upper ? 'LVL' : 'lvl'}
        </span>
    );
};

export default LevelBadge;
